import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Plus, Trash2, ArrowUp, ArrowDown, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { services } from '@/pages/services/serviceData';

interface ChecklistItem {
  id: string;
  service_slug: string;
  phase: string;
  position: number;
  title: string;
  description: string;
}

const ChecklistsPage = () => {
  const [selectedService, setSelectedService] = useState(services[0].slug);
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [newPhase, setNewPhase] = useState<'scoping' | 'implementation'>('scoping');
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const fetchItems = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('service_checklist_items')
      .select('*')
      .eq('service_slug', selectedService)
      .order('phase')
      .order('position');
    if (error) toast.error(error.message);
    else setItems((data as ChecklistItem[]) || []);
    setLoading(false);
  };

  useEffect(() => { fetchItems(); }, [selectedService]);

  const scopingItems = items.filter(i => i.phase === 'scoping');
  const implItems = items.filter(i => i.phase === 'implementation');

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    const phaseItems = items.filter(i => i.phase === newPhase);
    const maxPos = phaseItems.length > 0 ? Math.max(...phaseItems.map(i => i.position)) : 0;
    const { error } = await supabase.from('service_checklist_items').insert({
      service_slug: selectedService,
      phase: newPhase,
      position: maxPos + 1,
      title: newTitle.trim(),
      description: newDesc.trim(),
    });
    if (error) toast.error(error.message);
    else { toast.success('Item added'); setNewTitle(''); setNewDesc(''); fetchItems(); }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('service_checklist_items').delete().eq('id', id);
    if (error) toast.error(error.message);
    else { toast.success('Deleted'); fetchItems(); }
  };

  const handleMove = async (item: ChecklistItem, direction: 'up' | 'down') => {
    const phaseItems = items.filter(i => i.phase === item.phase).sort((a, b) => a.position - b.position);
    const idx = phaseItems.findIndex(i => i.id === item.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= phaseItems.length) return;
    const other = phaseItems[swapIdx];
    await Promise.all([
      supabase.from('service_checklist_items').update({ position: other.position }).eq('id', item.id),
      supabase.from('service_checklist_items').update({ position: item.position }).eq('id', other.id),
    ]);
    fetchItems();
  };

  const startEdit = (item: ChecklistItem) => {
    setEditingId(item.id);
    setEditTitle(item.title);
    setEditDesc(item.description);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const { error } = await supabase.from('service_checklist_items')
      .update({ title: editTitle, description: editDesc })
      .eq('id', editingId);
    if (error) toast.error(error.message);
    else { toast.success('Updated'); setEditingId(null); fetchItems(); }
  };

  const renderItems = (phaseItems: ChecklistItem[], phaseLabel: string) => (
    <div className="space-y-2">
      <h3 className="text-base font-semibold" style={{ color: 'var(--ink)' }}>{phaseLabel}</h3>
      {phaseItems.length === 0 && <p className="text-sm" style={{ color: 'var(--slate)' }}>No items yet.</p>}
      {phaseItems.sort((a, b) => a.position - b.position).map((item, idx) => (
        <Card key={item.id} className="p-3 flex items-start gap-3">
          <span className="text-xs font-mono mt-1 min-w-[20px] text-center" style={{ color: 'var(--slate)' }}>{idx + 1}</span>
          {editingId === item.id ? (
            <div className="flex-1 space-y-2">
              <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="text-sm" />
              <Textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} className="text-sm" rows={2} />
              <div className="flex gap-2">
                <Button size="sm" onClick={saveEdit}><Check className="w-3 h-3 mr-1" />Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}><X className="w-3 h-3 mr-1" />Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>{item.title}</p>
              {item.description && <p className="text-xs mt-0.5" style={{ color: 'var(--slate)' }}>{item.description}</p>}
            </div>
          )}
          {editingId !== item.id && (
            <div className="flex items-center gap-1 shrink-0">
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleMove(item, 'up')} disabled={idx === 0}>
                <ArrowUp className="w-3 h-3" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleMove(item, 'down')} disabled={idx === phaseItems.length - 1}>
                <ArrowDown className="w-3 h-3" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(item)}>
                <Pencil className="w-3 h-3" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDelete(item.id)}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          )}
        </Card>
      ))}
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-fraunces font-bold" style={{ color: 'var(--ink)' }}>Checklist Templates</h1>
      </div>

      <Select value={selectedService} onValueChange={setSelectedService}>
        <SelectTrigger className="w-80">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {services.map(s => (
            <SelectItem key={s.slug} value={s.slug}>{s.title}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {loading ? (
        <div className="animate-pulse py-8 text-center" style={{ color: 'var(--slate)' }}>Loading…</div>
      ) : (
        <div className="grid gap-8 md:grid-cols-2">
          {renderItems(scopingItems, '🔍 Scoping Questions')}
          {renderItems(implItems, '🚀 Implementation Steps')}
        </div>
      )}

      {/* Add new item */}
      <Card className="p-4 space-y-3" style={{ borderColor: 'var(--line)' }}>
        <h3 className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Add New Item</h3>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <Select value={newPhase} onValueChange={(v: 'scoping' | 'implementation') => setNewPhase(v)}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="scoping">Scoping</SelectItem>
                <SelectItem value="implementation">Implementation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Input placeholder="Item title" value={newTitle} onChange={e => setNewTitle(e.target.value)} />
          </div>
          <Button onClick={handleAdd} disabled={!newTitle.trim()}>
            <Plus className="w-4 h-4 mr-1" />Add
          </Button>
        </div>
        <Textarea placeholder="Description (optional)" value={newDesc} onChange={e => setNewDesc(e.target.value)} rows={2} />
      </Card>

      <div className="flex gap-2">
        <Badge variant="secondary">{scopingItems.length} scoping</Badge>
        <Badge variant="secondary">{implItems.length} implementation</Badge>
        <Badge>{items.length} total</Badge>
      </div>
    </div>
  );
};

export default ChecklistsPage;
